import { lazy, Suspense, useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { Sidebar } from './components/Sidebar';
import {
  Button,
  Callout,
  Dialog,
  Input,
  Menu,
  MenuItem,
  MenuDivider,
  Popover,
  QuietStatus,
} from './components/primitives';
import { ConnectionDesignerHorizontal } from './components/brand';
import { getPersistedSelectedSampleWeekId } from './hooks/useSampleWeeks';
import { useMainContentScrollFix } from './hooks/useMainContentScrollFix';
import { TenantContext, useTenantConfig, useTenantConfigLoader } from './hooks/useTenantConfig';
import { useInvestigationsAccess } from './hooks/useInvestigationsAccess';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { authService } from './services/authService';
import EnvironmentBadge from './components/EnvironmentBadge';
import { getUserDisplayIdentifier } from './utils/userIdentity';
import { loadLoginPage } from './utils/loadLoginPage';
import { LoginRoute } from './components/LoginRoute';


import './theme/theme.css';
import './App.css';

const ScheduleLoader = lazy(() => import('./pages/ScheduleLoader'));
const ScheduleExplorer = lazy(() => import('./pages/ScheduleExplorer'));
const MultipleFlightPairs = lazy(() => import('./pages/MultipleFlightPairs'));
const ScheduleComparison = lazy(() => import('./pages/ScheduleComparison'));
const HubAnalysis = lazy(() => import('./pages/HubAnalysis'));
const ConnectivityRules = lazy(() => import('./pages/ConnectivityRules'));
const AirportDefinitions = lazy(() => import('./pages/AirportDefinitions'));
const RegionDefinitions = lazy(() => import('./pages/RegionDefinitions'));
const CountryDefinitions = lazy(() => import('./pages/CountryDefinitions'));
const MinimumConnectionTimes = lazy(() => import('./pages/MinimumConnectionTimes'));
const PartnershipAnalysis = lazy(() => import('./pages/PartnershipAnalysis'));
const HubOptimizer = lazy(() => import('./pages/HubOptimizer'));
const ScheduleVisualize = lazy(() => import('./pages/ScheduleVisualize'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const UserPreferences = lazy(() => import('./pages/UserPreferences'));
const DesignSystem = lazy(() => import('./pages/DesignSystem'));
const Workspaces = lazy(() => import('./pages/Workspaces'));
const Investigations = lazy(() => import('./pages/Investigations'));
const ArtifactViewer = lazy(() => import('./pages/ArtifactViewer'));

const routeFallback = (
  <div className="lm-auth-loading">
    <QuietStatus title="Loading page" detail="Preparing workspace" size="lg" />
  </div>
);

function AppContent({ isTenantConfigLoading }: { isTenantConfigLoading: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const mainContentRef = useMainContentScrollFix();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { authentication, features } = useTenantConfig();
  const investigationsAllowed = useInvestigationsAccess();
  const { isAuthenticated, isLoading, isAdmin, logout, user, refreshUser } = useAuth();

  useEffect(() => {
    if (isLoading && location.pathname === '/login') {
      // Overlap the small public page chunk with session restoration. Rendering
      // still waits for auth below; a speculative failure must not reject here.
      void loadLoginPage().catch(() => {});
    }
  }, [isLoading, location.pathname]);

  // Change password dialog state
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);

  const mustChangePassword = user?.must_change_password ?? false;
  const userIdentifier = getUserDisplayIdentifier(user);
  const passwordRecoveryEnabled = authentication?.password_recovery
    ?? authentication?.mode !== 'trial_username';
  const canManageUsers = isAdmin && (authentication?.user_management ?? true);

  const handleCloseChangePassword = useCallback(() => {
    if (mustChangePassword) return; // Cannot dismiss when forced
    setChangePasswordOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setChangePasswordError('');
    setChangePasswordSuccess(false);
  }, [mustChangePassword]);

  const handleChangePassword = useCallback(async () => {
    setChangePasswordError('');
    if (newPassword !== confirmPassword) {
      setChangePasswordError('New passwords do not match.');
      return;
    }
    setChangePasswordLoading(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      await refreshUser();
      setChangePasswordSuccess(true);
    } catch (err: unknown) {
      const error = err as Record<string, string[] | string>;
      const msg =
        error?.current_password?.[0] ||
        error?.new_password?.[0] ||
        error?.detail ||
        'Failed to change password.';
      setChangePasswordError(typeof msg === 'string' ? msg : String(msg));
    } finally {
      setChangePasswordLoading(false);
    }
  }, [currentPassword, newPassword, confirmPassword, refreshUser]);

  const sampleWeekPathMatch = location.pathname.match(/^\/(?:explore|edit|visualize)\/([^/]+)/);
  const currentSampleWeekId = sampleWeekPathMatch?.[1] || '';

  if (isLoading || isTenantConfigLoading) {
    return (
      <div className="lm-auth-loading">
        <QuietStatus title="Loading workspace" detail="Checking session" size="lg" />
      </div>
    );
  }

  const isAuthRoute = location.pathname === '/login'
    || location.pathname === '/forgot-password'
    || location.pathname.startsWith('/reset-password/');
  const isPublicRoute = isAuthRoute || location.pathname === '/design-system';

  if (!isAuthenticated && !isPublicRoute) {
    return <Navigate to="/login" replace />;
  }

  if (isAuthenticated && location.pathname === '/login') {
    return <Navigate to="/" replace />;
  }

  // Render public auth pages without app chrome
  if (isAuthRoute) {
    return (
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route
            path="/forgot-password"
            element={passwordRecoveryEnabled ? <ForgotPassword /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/reset-password/:uid/:token"
            element={passwordRecoveryEnabled ? <ResetPassword /> : <Navigate to="/login" replace />}
          />
        </Routes>
      </Suspense>
    );
  }

  // Render design-system without auth so foundation can be verified
  if (!isAuthenticated && location.pathname === '/design-system') {
    return (
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/design-system" element={<DesignSystem />} />
        </Routes>
      </Suspense>
    );
  }

  const getSelectedTabId = () => {
    if (location.pathname === '/') return 'loader';
    if (location.pathname.startsWith('/edit')) return 'calculator';
    if (location.pathname.startsWith('/explore')) return 'explorer';
    if (location.pathname.startsWith('/visualize')) return 'visualize';
    if (location.pathname.startsWith('/multiple-flight-pairs')) return 'multiple-flight-pairs';
    if (location.pathname.startsWith('/schedule-comparison')) return 'schedule-comparison';
    if (location.pathname.startsWith('/hub-analysis')) return 'hub-analysis';
    if (location.pathname.startsWith('/connectivity-rules')) return 'connectivity-rules';
    if (location.pathname.startsWith('/airport-definitions')) return 'airport-definitions';
    if (location.pathname.startsWith('/region-definitions')) return 'region-definitions';
    if (location.pathname.startsWith('/country-definitions')) return 'country-definitions';
    if (location.pathname.startsWith('/minimum-connection-times')) return 'minimum-connection-times';
    if (location.pathname.startsWith('/partnership-analysis')) return 'partnership-analysis';
    if (location.pathname.startsWith('/hub-optimizer')) return 'hub-optimizer';
    if (location.pathname.startsWith('/designer')) return 'designer';
    if (location.pathname.startsWith('/workspaces')) return 'workspaces';
    // Saved reports open from a workspace.
    if (location.pathname.startsWith('/artifacts')) return 'workspaces';
    if (location.pathname.startsWith('/user-preferences')) return 'user-preferences';
    if (location.pathname.startsWith('/admin')) return 'admin';
    if (location.pathname.startsWith('/design-system')) return 'design-system';
    return 'loader';
  };

  const handleTabChange = (tabId: string) => {
    switch (tabId) {
      case 'loader':
        navigate('/');
        break;
      case 'explorer':
        if (currentSampleWeekId) {
          navigate(`/explore/${currentSampleWeekId}`);
        } else {
          const persistedSampleWeekId = getPersistedSelectedSampleWeekId();
          navigate(persistedSampleWeekId ? `/explore/${persistedSampleWeekId}` : '/explore/');
        }
        break;
      case 'visualize':
        if (currentSampleWeekId) {
          navigate(`/visualize/${currentSampleWeekId}`);
        } else {
          const persistedSampleWeekId = getPersistedSelectedSampleWeekId();
          navigate(persistedSampleWeekId ? `/visualize/${persistedSampleWeekId}` : '/visualize/');
        }
        break;
      case 'calculator':
        if (currentSampleWeekId) {
          navigate(`/edit/${currentSampleWeekId}`);
        } else {
          const persistedSampleWeekId = getPersistedSelectedSampleWeekId();
          navigate(persistedSampleWeekId ? `/edit/${persistedSampleWeekId}` : '/edit/');
        }
        break;
      case 'multiple-flight-pairs':
        navigate('/multiple-flight-pairs');
        break;
      case 'schedule-comparison':
        navigate('/schedule-comparison');
        break;
      case 'hub-analysis':
        navigate('/hub-analysis');
        break;
      case 'connectivity-rules':
        navigate('/connectivity-rules');
        break;
      case 'airport-definitions':
        navigate('/airport-definitions');
        break;
      case 'region-definitions':
        navigate('/region-definitions');
        break;
      case 'country-definitions':
        navigate('/country-definitions');
        break;
      case 'minimum-connection-times':
        navigate('/minimum-connection-times');
        break;
      case 'partnership-analysis':
        navigate('/partnership-analysis');
        break;
      case 'hub-optimizer':
        navigate('/hub-optimizer');
        break;
      case 'designer':
        navigate('/designer');
        break;
      case 'workspaces':
        navigate('/workspaces');
        break;
      case 'user-preferences':
        navigate('/user-preferences');
        break;
      case 'admin':
        navigate('/admin');
        break;
    }
  };

  const submitDisabled = !currentPassword || !newPassword || !confirmPassword;

  return (
    <div className="lm-app-layout">
      <header className="lm-navbar">
        <div className="lm-navbar__left">
          <ConnectionDesignerHorizontal height={24} className="lm-navbar__lockup" />
          <EnvironmentBadge />
        </div>

        <div className="lm-navbar__right">
          {user?.role && user.role !== 'member' && (
            <span className={`lm-navbar__role lm-navbar__role--${user.role}`}>{user.role}</span>
          )}
          <Popover
            content={
              <Menu>
                <MenuItem
                  icon={<VpnKeyRoundedIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setChangePasswordOpen(true)}
                >
                  Change Password
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  intent="danger"
                  icon={<LogoutRoundedIcon sx={{ fontSize: 14 }} />}
                  onClick={logout}
                >
                  Sign Out
                </MenuItem>
              </Menu>
            }
          >
            <button type="button" className="lm-navbar__user">
              <span className="lm-navbar__avatar" aria-hidden>
                {userIdentifier[0]?.toUpperCase() ?? '·'}
              </span>
              <span className="lm-navbar__email">{userIdentifier}</span>
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
                <path
                  d="M2 3.5l3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </Popover>
        </div>
      </header>

      <div className="lm-app-content">
        <Sidebar
          collapsed={sidebarCollapsed}
          selectedTab={getSelectedTabId()}
          onTabChange={handleTabChange}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          isAdmin={canManageUsers}
          canManageConnectivity={isAdmin}
        />

        <main ref={mainContentRef} className="main-content">
          <Suspense fallback={routeFallback}>
            <Routes>
              <Route path="/" element={<ScheduleLoader />} />
              {features.schedule_explorer && <>
                <Route path="/explore" element={<ScheduleExplorer />} />
                <Route path="/explore/:sampleWeekId" element={<ScheduleExplorer />} />
                <Route path="/edit" element={<ScheduleExplorer initialTab="edit" />} />
                <Route path="/edit/:sampleWeekId" element={<ScheduleExplorer initialTab="edit" />} />
              </>}
              {features.schedule_visualize && <>
                <Route path="/visualize" element={<ScheduleVisualize />} />
                <Route path="/visualize/:sampleWeekId" element={<ScheduleVisualize />} />
              </>}
              {features.connection_analysis && (
                <Route path="/multiple-flight-pairs" element={<MultipleFlightPairs />} />
              )}
              {features.schedule_comparison && (
                <Route path="/schedule-comparison" element={<ScheduleComparison />} />
              )}
              {features.hub_reports && (
                <Route path="/hub-analysis" element={<HubAnalysis />} />
              )}
              {features.od_itinerary_compare && isAdmin && <Route path="/connectivity-rules" element={<ConnectivityRules />} />}
              <Route path="/airport-definitions" element={<AirportDefinitions />} />
              <Route path="/region-definitions" element={<RegionDefinitions />} />
              <Route path="/country-definitions" element={<CountryDefinitions />} />
              {features.mct_management && (
                <Route path="/minimum-connection-times" element={<MinimumConnectionTimes />} />
              )}
              {features.partnership_analysis && (
                <Route path="/partnership-analysis" element={<PartnershipAnalysis />} />
              )}
              {features.bank_optimizer && (
                <Route path="/hub-optimizer" element={<HubOptimizer />} />
              )}
              {features.workspaces && <>
                <Route path="/workspaces" element={<Workspaces />} />
                <Route path="/workspaces/:workspaceId" element={<Workspaces />} />
              </>}
              {(features.agent_api || investigationsAllowed) && <>
                <Route path="/designer" element={<Investigations />} />
                <Route path="/designer/:investigationId" element={<Investigations />} />
              </>}
              {(features.agent_api || features.agent_investigations) && (
                <Route path="/artifacts/:artifactId" element={<ArtifactViewer />} />
              )}
              <Route path="/user-preferences" element={<UserPreferences />} />
              <Route path="/design-system" element={<DesignSystem />} />
              {canManageUsers && (
                <Route path="/admin" element={<AdminPanel />} />
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>

      <Dialog
        isOpen={changePasswordOpen || mustChangePassword}
        onClose={handleCloseChangePassword}
        title={mustChangePassword ? 'Change your temporary password' : 'Change password'}
        size="sm"
        closeOnEscape={!mustChangePassword}
        closeOnBackdrop={!mustChangePassword}
        hideCloseButton={mustChangePassword}
        footer={
          changePasswordSuccess ? (
            <Button variant="accent" onClick={handleCloseChangePassword}>Close</Button>
          ) : (
            <>
              {!mustChangePassword && (
                <Button variant="ghost" onClick={handleCloseChangePassword}>Cancel</Button>
              )}
              <Button
                variant="accent"
                onClick={handleChangePassword}
                loading={changePasswordLoading}
                disabled={submitDisabled}
              >
                Change password
              </Button>
            </>
          )
        }
      >
        {changePasswordSuccess ? (
          <Callout intent="success" title="Password changed">
            You can now use the new password on next sign-in.
          </Callout>
        ) : (
          <div className="lm-form">
            {changePasswordError && (
              <Callout intent="danger" title="Could not change password">
                {changePasswordError}
              </Callout>
            )}
            {mustChangePassword && !changePasswordError && (
              <Callout intent="info">
                Your account was issued a temporary password. Set a new one to continue.
              </Callout>
            )}
            <label className="lm-form__field">
              <span className="lm-form__label">Current password</span>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoFocus
                fullWidth
              />
            </label>
            <label className="lm-form__field">
              <span className="lm-form__label">New password</span>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
              />
            </label>
            <label className="lm-form__field">
              <span className="lm-form__label">Confirm new password</span>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !submitDisabled && handleChangePassword()}
                fullWidth
              />
            </label>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function App() {
  const { config, isLoading: isTenantConfigLoading } = useTenantConfigLoader();

  return (
    <TenantContext.Provider value={config}>
      <AuthProvider>
        <Router>
          <AppContent isTenantConfigLoading={isTenantConfigLoading} />
        </Router>
      </AuthProvider>
    </TenantContext.Provider>
  );
}

export default App;
